import { useState, useMemo } from 'react';
import { Printer, Download, TrendingUp, CreditCard, Users, IndianRupee } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { formatDate, formatCurrency } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';

// ── Date range helpers ─────────────────────────────────────────
function getRange(period) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'today': {
      return [today, new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)];
    }
    case 'this_week': {
      const day = today.getDay(); // 0=Sun
      const start = new Date(today); start.setDate(today.getDate() - day);
      const end   = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23,59,59);
      return [start, end];
    }
    case 'last_week': {
      const day = today.getDay();
      const end   = new Date(today); end.setDate(today.getDate() - day - 1); end.setHours(23,59,59);
      const start = new Date(end);   start.setDate(end.getDate() - 6); start.setHours(0,0,0);
      return [start, end];
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end   = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
      return [start, end];
    }
    case 'last_3_months': {
      const start = new Date(today); start.setMonth(today.getMonth() - 3); start.setDate(1);
      return [start, new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)];
    }
    case 'last_6_months': {
      const start = new Date(today); start.setMonth(today.getMonth() - 6); start.setDate(1);
      return [start, new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)];
    }
    case 'academic_year': {
      // Academic year: May 1 → April 30
      const m = today.getMonth() + 1; // 1-based
      const y = today.getFullYear();
      const startYear = m >= 5 ? y : y - 1;
      const start = new Date(startYear, 4, 1);          // May 1
      const end   = new Date(startYear + 1, 3, 30, 23, 59, 59); // Apr 30 next year
      return [start, end];
    }
    default:
      return [new Date(0), new Date()];
  }
}

const PERIOD_LABELS = {
  today:          'Today',
  this_week:      'This Week',
  last_week:      'Last Week',
  this_month:     'This Month',
  last_3_months:  'Last 3 Months',
  last_6_months:  'Last 6 Months',
  academic_year:  'This Academic Year (May–Apr)',
};

const TYPE_LABEL = {
  current:      'Tuition Fee',
  prevBalance:  'Previous Balance',
  transportFee: 'Transport Fee',
  admissionFee: 'Admission Fee',
};

const TYPE_COLOR = {
  current:      'bg-purple-50  text-purple-700',
  prevBalance:  'bg-orange-50  text-orange-700',
  transportFee: 'bg-blue-50    text-blue-700',
  admissionFee: 'bg-green-50   text-green-700',
};

const MODE_COLOR = {
  cash:   'bg-green-100  text-green-700',
  cheque: 'bg-yellow-100 text-yellow-700',
  online: 'bg-blue-100   text-blue-700',
  UPI:    'bg-indigo-100 text-indigo-700',
  DD:     'bg-gray-100   text-gray-600',
};

export default function FeeCollectionReport() {
  const { feePayments, students, classes } = useApp();
  const [period,      setPeriod]      = useState('this_month');
  const [filterType,  setFilterType]  = useState('all');
  const [filterMode,  setFilterMode]  = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [search,      setSearch]      = useState('');

  // Compute date range
  const [rangeStart, rangeEnd] = useMemo(() => getRange(period), [period]);

  // ── Filter payments ──────────────────────────────────────────
  const filtered = useMemo(() => {
    return feePayments.filter(p => {
      const d = new Date(p.paidDate);
      if (d < rangeStart || d > rangeEnd) return false;
      if (filterType !== 'all' && p.type !== filterType) return false;
      if (filterMode !== 'all' && p.paymentMode !== filterMode) return false;

      const stu = students.find(s => s.id === p.studentId);
      if (filterClass !== 'all' && stu?.classId !== filterClass) return false;
      if (search) {
        const name = stu ? `${stu.firstName} ${stu.lastName}`.toLowerCase() : '';
        const adm  = stu?.admissionNumber?.toLowerCase() || '';
        const ref  = p.transactionRef?.toLowerCase() || '';
        if (!name.includes(search.toLowerCase()) && !adm.includes(search.toLowerCase()) && !ref.includes(search.toLowerCase())) return false;
      }
      return true;
    }).sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate));
  }, [feePayments, rangeStart, rangeEnd, filterType, filterMode, filterClass, search, students]);

  // ── Summary stats ────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = filtered.reduce((s, p) => s + Number(p.amount), 0);
    const byType = {};
    const byMode = {};
    const uniqueStudents = new Set();
    filtered.forEach(p => {
      byType[p.type]        = (byType[p.type]        || 0) + Number(p.amount);
      byMode[p.paymentMode] = (byMode[p.paymentMode] || 0) + Number(p.amount);
      if (p.studentId) uniqueStudents.add(p.studentId);
    });
    return { total, byType, byMode, uniqueStudents: uniqueStudents.size };
  }, [filtered]);

  // Helper
  const stuInfo = (id) => {
    const s = students.find(x => x.id === id);
    if (!s) return { name: '—', cls: '—', adm: '—' };
    const c = classes.find(x => x.id === s.classId);
    return { name: `${s.firstName} ${s.lastName}`, cls: c ? `${c.name}-${c.section}` : '—', adm: s.admissionNumber || '—' };
  };

  const handlePrint = async () => {
    const school = await getSchoolInfo();
    const rows = filtered.map(p => {
      const { name, cls, adm } = stuInfo(p.studentId);
      return `<tr>
        <td>${formatDate(p.paidDate)}</td>
        <td>${adm}</td>
        <td>${name}</td>
        <td>${cls}</td>
        <td>${TYPE_LABEL[p.type] || p.type}</td>
        <td>${p.feeLabel || ''}</td>
        <td style="text-align:right">₹${Number(p.amount).toLocaleString('en-IN')}</td>
        <td>${p.paymentMode?.toUpperCase()}</td>
        <td>${p.transactionRef || '—'}</td>
      </tr>`;
    }).join('');

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Fee Collection Report</title>
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;600;700&display=swap" rel="stylesheet">
    <style>body{font-family:'Roboto',sans-serif;padding:16px;font-size:12px;color:#111}
    table{width:100%;border-collapse:collapse;font-size:12px}
    th{background:#f3e8ff;color:#6b21a8;padding:6px 8px;text-align:left;border:1px solid #e9d5ff}
    td{padding:5px 8px;border:1px solid #f0f0f0}tr:nth-child(even){background:#fafafa}
    .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px}
    .stat{background:#f9f5ff;border:1px solid #e9d5ff;border-radius:8px;padding:10px}
    .stat h3{margin:0;font-size:11px;color:#9333ea}.stat p{margin:4px 0 0;font-size:18px;font-weight:bold;color:#4c1d95}
    @page{margin:3mm}@media print{.no-print{display:none}body{padding:4px}*{color:#000!important}}</style></head><body>
    ${schoolHeader(school, '#6b21a8', `Fee Collection Report &mdash; ${PERIOD_LABELS[period]}`)}

    <div class="summary">
      <div class="stat"><h3>Total Collected</h3><p>₹${Math.round(stats.total).toLocaleString('en-IN')}</p></div>
      <div class="stat"><h3>Payments</h3><p>${filtered.length}</p></div>
      <div class="stat"><h3>Students</h3><p>${stats.uniqueStudents}</p></div>
      <div class="stat"><h3>Period</h3><p style="font-size:13px">${PERIOD_LABELS[period]}</p></div>
    </div>
    <table><thead><tr><th>Date</th><th>Adm No.</th><th>Student</th><th>Class</th>
    <th>Type</th><th>Label</th><th>Amount</th><th>Mode</th><th>Ref</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="6" style="text-align:right;font-weight:bold">Total</td>
    <td style="text-align:right;font-weight:bold">₹${Math.round(stats.total).toLocaleString('en-IN')}</td>
    <td colspan="2"></td></tr></tfoot>
    </table></body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Fee Collection Report</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {filtered.length} payment{filtered.length !== 1 ? 's' : ''} · {PERIOD_LABELS[period]}
          </p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
          <Printer size={14} /> Print / Export
        </button>
      </div>

      {/* Period selector — prominent */}
      <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4 mb-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Select Period</p>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setPeriod(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                period === key
                  ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-600'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard icon={<IndianRupee size={18} />} label="Total Collected" value={`₹${Math.round(stats.total).toLocaleString('en-IN')}`} color="purple" />
        <StatCard icon={<CreditCard size={18} />}  label="Transactions"   value={filtered.length}          color="blue" />
        <StatCard icon={<Users size={18} />}        label="Students"       value={stats.uniqueStudents}      color="green" />
        <StatCard icon={<TrendingUp size={18} />}   label="Avg per Entry"  value={filtered.length > 0 ? `₹${Math.round(stats.total/filtered.length).toLocaleString('en-IN')}` : '₹0'} color="orange" />
      </div>

      {/* Breakdown by type & mode */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">By Fee Type</p>
          {Object.entries(stats.byType).length === 0
            ? <p className="text-sm text-gray-400">No data</p>
            : Object.entries(stats.byType).sort((a,b) => b[1]-a[1]).map(([type, amt]) => (
              <div key={type} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${TYPE_COLOR[type] || 'bg-gray-100 text-gray-600'}`}>
                  {TYPE_LABEL[type] || type}
                </span>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-800">₹{Math.round(amt).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-400">{stats.total > 0 ? Math.round(amt/stats.total*100) : 0}%</p>
                </div>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">By Payment Mode</p>
          {Object.entries(stats.byMode).length === 0
            ? <p className="text-sm text-gray-400">No data</p>
            : Object.entries(stats.byMode).sort((a,b) => b[1]-a[1]).map(([mode, amt]) => (
              <div key={mode} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${MODE_COLOR[mode] || 'bg-gray-100 text-gray-600'}`}>
                  {mode?.toUpperCase()}
                </span>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-800">₹{Math.round(amt).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-400">{stats.total > 0 ? Math.round(amt/stats.total*100) : 0}%</p>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Filters row */}
      <div className="flex gap-3 flex-wrap mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search student / adm no / ref…"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 w-64" />
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="all">All Classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="all">All Types</option>
          {Object.entries(TYPE_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterMode} onChange={e => setFilterMode(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="all">All Modes</option>
          {['cash','cheque','online','UPI','DD'].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
        </select>
        {(filterType !== 'all' || filterMode !== 'all' || filterClass !== 'all' || search) && (
          <button onClick={() => { setFilterType('all'); setFilterMode('all'); setFilterClass('all'); setSearch(''); }}
            className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            Clear filters
          </button>
        )}
      </div>

      {/* Payments table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Date','Adm No.','Student','Class','Type','Amount','Mode','Reference'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                No payments found for {PERIOD_LABELS[period]}
              </td></tr>
            ) : filtered.map(p => {
              const { name, cls, adm } = stuInfo(p.studentId);
              return (
                <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(p.paidDate)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{adm}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{name}</td>
                  <td className="px-4 py-3 text-gray-500">{cls}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${TYPE_COLOR[p.type] || 'bg-gray-100 text-gray-600'}`}>
                      {p.feeLabel || TYPE_LABEL[p.type] || p.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    ₹{Number(p.amount).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${MODE_COLOR[p.paymentMode] || 'bg-gray-100 text-gray-600'}`}>
                      {p.paymentMode?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{p.transactionRef || '—'}</td>
                </tr>
              );
            })}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-purple-50 border-t-2 border-purple-100">
              <tr>
                <td colSpan={5} className="px-4 py-3 text-right font-bold text-gray-700">Total ({filtered.length} payments)</td>
                <td className="px-4 py-3 font-bold text-purple-700 text-base">
                  ₹{Math.round(stats.total).toLocaleString('en-IN')}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    blue:   'bg-blue-50   text-blue-600   border-blue-100',
    green:  'bg-green-50  text-green-600  border-green-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
  };
  return (
    <div className={`rounded-xl border shadow-sm p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1 opacity-70">{icon}<span className="text-xs font-semibold uppercase tracking-wide">{label}</span></div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
